import { useEffect, useSyncExternalStore } from 'react';

import { getStatusFromStorage } from '@/components/PagePopup/utils/getStatusFromStorage';
import { setAsDismissedInStorage } from '@/components/PagePopup/utils/setAsDismissedInStorage';
import { useWhitelistedChannels } from '@/components/UBSafelist/hooks/useWhitelistedChannels';
import { useHasPermission } from '@/hooks/auth';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import { log } from '@/lib/utils';

/**
 * sessionStorage key that suppresses the prompt for the rest of the session.
 * Written by `PageDialog` when the prompt is dismissed (Esc / X / drag-down)
 * and by `blockSafelistPromptForSession` when the user has just subscribed —
 * either way the prompt returns in the next session.
 */
export const SAFELIST_PROMPT_STORAGE_KEY = 'safelist-channel-prompt';

/**
 * localStorage key set when the user clicks "Don't show this message again".
 *
 * TODO(DEV-6225): persist this against the user's account once the backend
 * endpoint exists, so the opt-out follows the user across devices.
 */
export const SAFELIST_PROMPT_OPT_OUT_KEY = 'safelist-channel-prompt-opt-out';

/**
 * Suppresses the safelist prompt for the current session. Called from the
 * subscription success paths so a user who has just subscribed isn't
 * immediately prompted — the prompt first appears in their next session.
 */
export const blockSafelistPromptForSession = () => {
  setAsDismissedInStorage({
    key: SAFELIST_PROMPT_STORAGE_KEY,
    storage: 'sessionStorage',
  });
};

/**
 * The opt-out must be read live rather than cached in mount-time state: the
 * per-session dismissal that hides the prompt after clicking "Don't show this
 * message again" lives in sessionStorage, which is per-tab. A tab that
 * mounted before the opt-out was clicked (login never reloads the page) would
 * otherwise hold a stale "not opted out" and re-show the prompt (DEV-6843).
 * The storage event fires in every other tab when localStorage changes, so
 * subscribing to it keeps all open tabs in sync.
 */
const subscribeToStorage = (onStoreChange: () => void) => {
  window.addEventListener('storage', onStoreChange);
  return () => window.removeEventListener('storage', onStoreChange);
};

const getHasOptedOut = () =>
  getStatusFromStorage({
    storageKey: SAFELIST_PROMPT_OPT_OUT_KEY,
    storageType: 'localStorage',
  }) === true;

// SSR snapshot: pessimistic, so the server never counts the prompt eligible.
const getServerHasOptedOut = () => true;

/**
 * Decides whether the safelist-channel prompt should enter the dialogue
 * queue. Eligible when the user can safelist (`safelist_yt` permission), is
 * confirmed to have no safelisted channels yet, has no overdue payment (the
 * failed-payment popup takes precedence), and hasn't opted out permanently.
 *
 * Per-session suppression (dismissal, or having subscribed this session via
 * `blockSafelistPromptForSession`) is handled by the queue itself through
 * the prompt's sessionStorage `storageKey`.
 */
export const useSafelistChannelPromptEligibility = (): boolean => {
  const hasSafelistPermission = useHasPermission('safelist_yt');
  const {
    data: whitelistedChannels,
    isSuccess: hasLoadedChannels,
    status: channelsStatus,
    fetchStatus: channelsFetchStatus,
  } = useWhitelistedChannels();
  const { overduePayment } = useOverduePayment();
  const hasOptedOut = useSyncExternalStore(
    subscribeToStorage,
    getHasOptedOut,
    getServerHasOptedOut
  );

  // Only a successful empty response counts. A failed fetch says nothing
  // about the user's channels, and prompting on it had users re-adding
  // channels they already had (DEV-7130).
  const hasNoSafelistedChannels =
    hasLoadedChannels && whitelistedChannels.length === 0;

  const isEligible =
    hasSafelistPermission &&
    !hasOptedOut &&
    !overduePayment &&
    hasNoSafelistedChannels;

  const safelistedChannelCount = whitelistedChannels.length;

  // Dev-only debug logging: whether the dialog should show and, if not, why.
  useEffect(() => {
    const blockedBy = [
      !hasSafelistPermission &&
        'no safelist_yt permission (not a paying subscriber)',
      hasOptedOut &&
        `opted out permanently (localStorage '${SAFELIST_PROMPT_OPT_OUT_KEY}')`,
      !!overduePayment &&
        'overdue payment (failed-payment popup takes precedence)',
      channelsStatus === 'pending' &&
        `safelisted channels not loaded yet (fetchStatus: '${channelsFetchStatus}' — 'idle' means the query is disabled and needs client-side login state and settings.user.id)`,
      channelsStatus === 'error' && 'safelisted channels failed to load',
      hasLoadedChannels &&
        safelistedChannelCount > 0 &&
        `already has ${safelistedChannelCount} safelisted channel(s)`,
    ].filter(Boolean);

    if (!isEligible) {
      log('🧪 [SafelistPrompt] dialog will NOT show:', blockedBy.join('; '));
      return;
    }

    const dismissedThisSession =
      getStatusFromStorage({
        storageKey: SAFELIST_PROMPT_STORAGE_KEY,
        storageType: 'sessionStorage',
      }) === true;

    if (dismissedThisSession) {
      log(
        '🧪 [SafelistPrompt] eligible, but dialog will NOT show this session:',
        `dismissed or subscribed this session (sessionStorage '${SAFELIST_PROMPT_STORAGE_KEY}') — it returns next session`
      );
      return;
    }

    log(
      '🧪 [SafelistPrompt] eligible — dialog WILL show on music pages (/music, /trending)'
    );
  }, [
    isEligible,
    hasSafelistPermission,
    hasOptedOut,
    overduePayment,
    hasLoadedChannels,
    safelistedChannelCount,
    channelsStatus,
    channelsFetchStatus,
  ]);

  return isEligible;
};
